import * as THREE from 'three';
import { Kit, place } from '../seasons/kit.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The people of Musgoviejo (Caras prestadas, docs/SAGAS.md): a crowd crossing the plaza on the night of lanterns.
 * Six kinds in the miniature style (a man in a hood, a woman in a shawl and long skirt, an old man on a stick, a
 * child, a woman carrying a lantern on a pole, a man in a wide hat), each one merged into a single vertex-coloured
 * body so the whole crowd draws as a handful of instanced meshes. The legs are apart (one shared leg, instanced
 * twice per villager, swung about the hip) so they stride. Impostors are built exactly like everyone else.
 * Feet at y = 0, facing +Z, a hero's height (about 1.75).
 */

export const VILLAGER_KINDS = 6;
/** The hip pivots the legs swing about (model units). */
export const HIP_Y = 0.8;
export const HIP_X = 0.085;
/** Where the lantern on a pole hangs (kind 4), for its glow. */
export const POLE_LANTERN = new THREE.Vector3(0.3, 1.95, 0.18);

const SKINS = ['#e2c3a8', '#c99f80', '#a87b5c', '#efd2bd', '#8c6248'];
/** Wool and linen, cold and muted (no gold, no beige): slate, moss, rust red, grey, a dark brown wool. */
const CLOTH = ['#4a5a6e', '#55644a', '#7a3a32', '#6a6e78', '#5a4636', '#3e4a5a', '#6e5464'];
const DARK = '#2a2c32';
const HAIR = ['#2a211c', '#4a3626', '#8a8a8a', '#1c1c20'];

const pick = <T>(a: readonly T[], n: number): T => a[((n % a.length) + a.length) % a.length] as T;

/** A villager's body (no legs) as one vertex-coloured geometry; `seed` varies its colours. */
export const villagerBody = (kind: number, seed = 0): THREE.BufferGeometry => {
  const k = new Kit();
  const skin = pick(SKINS, seed);
  const cloth = pick(CLOTH, seed * 3 + kind);
  const cloth2 = pick(CLOTH, seed * 5 + kind + 2);
  const hair = pick(HAIR, seed + kind);
  const old = kind === 2;
  const kid = kind === 3;
  const woman = kind === 1 || kind === 4;
  const hunch = old ? 0.22 : 0;
  const s = kid ? 0.68 : 1;
  const body = new THREE.Matrix4().compose(new THREE.Vector3(0, 0, 0), new THREE.Quaternion(), new THREE.Vector3(s, s, s));
  const up = (x: number, y: number, z: number, yaw = 0, sc: number | THREE.Vector3 = 1, tx = 0, tz = 0): THREE.Matrix4 => place(x, y, z, yaw, sc, tx, tz);
  // Hips and belt.
  k.add(new THREE.CylinderGeometry(0.15, 0.14, 0.14, 8), DARK, body, up(0, HIP_Y + 0.04, 0));
  if (woman) {
    // A long skirt to the ankles (over the legs), an apron.
    k.add(new THREE.CylinderGeometry(0.15, 0.26, 0.72, 10, 1, true), cloth2, body, up(0, 0.46, 0));
    k.add(new THREE.CircleGeometry(0.26, 10).rotateX(Math.PI / 2), cloth2, body, up(0, 0.1, 0));
    k.add(new THREE.BoxGeometry(0.2, 0.4, 0.02), '#9aa0a8', body, up(0, 0.62, 0.17, 0, 1, -0.12));
  }
  // The torso, leaning forward for the old man.
  const torso = up(0, HIP_Y + 0.3, hunch * 0.3, 0, 1, hunch);
  k.add(new THREE.CylinderGeometry(0.19, 0.16, 0.48, 8), cloth, body, torso);
  // A cloak or shawl over the shoulders.
  if (kind === 0 || kind === 2 || kind === 5) k.add(new THREE.CylinderGeometry(0.2, 0.32, 0.62, 9, 1, true), pick(CLOTH, seed + 4), body, up(0, HIP_Y + 0.24, hunch * 0.25 - 0.02, 0, 1, hunch));
  if (woman) k.add(new THREE.ConeGeometry(0.3, 0.32, 9, 1, true), pick(CLOTH, seed + 6), body, up(0, HIP_Y + 0.46, hunch * 0.3, 0, 1, hunch));
  // Arms: hanging sleeves, hands; the lantern-bearer's right arm raised to the pole, the old man's to his stick.
  for (const sd of [-1, 1]) {
    const raise = (kind === 4 || kind === 2) && sd > 0;
    const arm = up(sd * 0.21, HIP_Y + 0.44, hunch * 0.35, 0, 1, raise ? -0.9 : 0.05, sd * 0.12);
    k.add(new THREE.CylinderGeometry(0.055, 0.065, 0.42, 6).translate(0, -0.2, 0), cloth, body, arm);
    k.add(new THREE.SphereGeometry(0.05, 6, 5).translate(0, -0.43, 0), skin, body, arm);
  }
  // Head, neck, face.
  const head = up(0, HIP_Y + 0.68, hunch * 0.6 + 0.02, 0, 1, hunch * 0.5);
  k.add(new THREE.CylinderGeometry(0.05, 0.06, 0.08, 6), skin, body, up(0, HIP_Y + 0.58, hunch * 0.45));
  k.add(new THREE.SphereGeometry(0.115, 10, 8), skin, body, head);
  k.add(new THREE.BoxGeometry(0.03, 0.04, 0.03).translate(0, -0.01, 0.11), skin, body, head);
  // Hair, hood, kerchief, cap, hat.
  if (kind === 0) k.add(new THREE.SphereGeometry(0.135, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.62).translate(0, 0.01, -0.015), pick(CLOTH, seed + 4), body, head);
  else if (woman) {
    k.add(new THREE.SphereGeometry(0.128, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.55).translate(0, 0.01, -0.01), pick(CLOTH, seed + 1), body, head);
    k.add(new THREE.ConeGeometry(0.06, 0.16, 6).translate(0, -0.1, -0.11), pick(CLOTH, seed + 1), body, head);
  } else if (kind === 2) {
    k.add(new THREE.SphereGeometry(0.12, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.4), '#bfc2c8', body, head);
    k.add(new THREE.ConeGeometry(0.07, 0.16, 6).rotateX(Math.PI).translate(0, -0.13, 0.07), '#c8cacf', body, head);
  } else if (kind === 3) k.add(new THREE.CylinderGeometry(0.1, 0.12, 0.08, 8).translate(0, 0.1, 0), pick(CLOTH, seed + 2), body, head);
  else if (kind === 5) {
    k.add(new THREE.CylinderGeometry(0.26, 0.26, 0.02, 12).translate(0, 0.08, 0), DARK, body, head);
    k.add(new THREE.CylinderGeometry(0.11, 0.13, 0.13, 10).translate(0, 0.15, 0), DARK, body, head);
  } else k.add(new THREE.SphereGeometry(0.12, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), hair, body, head);
  // The old man's stick; the lantern on its pole (the paper shade is drawn by the glow, its frame here).
  if (kind === 2) k.add(new THREE.CylinderGeometry(0.018, 0.022, 1.0, 5), '#3a2a20', body, up(0.3, 0.5, 0.22, 0, 1, 0.12));
  if (kind === 4) {
    k.add(new THREE.CylinderGeometry(0.016, 0.02, 1.6, 5), '#3a2a20', body, up(0.27, 1.2, 0.15, 0, 1, 0.1));
    k.add(new THREE.CylinderGeometry(0.075, 0.06, 0.16, 8), '#e9e3dc', body, up(POLE_LANTERN.x, POLE_LANTERN.y, POLE_LANTERN.z));
    k.add(new THREE.ConeGeometry(0.08, 0.06, 8).translate(0, 0.1, 0), DARK, body, up(POLE_LANTERN.x, POLE_LANTERN.y, POLE_LANTERN.z));
  }
  const mesh = k.build(new THREE.MeshBasicMaterial(), 'villager');
  const geo = (mesh as THREE.Mesh).geometry;
  geo.computeBoundingSphere();
  return geo;
};

/** One leg from the hip down (pivot at the origin, hanging along −Y), boot at the foot, vertex-coloured. */
export const villagerLeg = (): THREE.BufferGeometry => {
  const k = new Kit();
  const m = new THREE.Matrix4();
  k.add(new THREE.CylinderGeometry(0.06, 0.05, HIP_Y - 0.08, 6).translate(0, -(HIP_Y - 0.08) / 2, 0), '#3a3c44', m);
  k.add(new THREE.BoxGeometry(0.1, 0.1, 0.18).translate(0, -HIP_Y + 0.05, 0.03), '#1e1c1c', m);
  const mesh = k.build(new THREE.MeshBasicMaterial(), 'villagerLeg');
  return (mesh as THREE.Mesh).geometry;
};

/** The crowd for the gallery: every kind in a row (a static model, legs standing). */
export const villagers = (o?: BaseOpts): Model => {
  const c = begin(o, 1.6, 77);
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true });
  const leg = villagerLeg();
  for (let i = 0; i < VILLAGER_KINDS; i++) {
    const x = (i - (VILLAGER_KINDS - 1) / 2) * 0.55;
    const b = new THREE.Mesh(villagerBody(i, i), mat);
    b.position.x = x;
    c.g.add(b);
    const s = i === 3 ? 0.68 : 1;
    for (const sd of [-1, 1]) {
      const l = new THREE.Mesh(leg, mat);
      l.position.set(x + sd * HIP_X * s, HIP_Y * s, 0);
      l.scale.setScalar(s);
      c.g.add(l);
    }
  }
  return done(c, 1.8);
};
